import React from 'react';
import {Pressable, StyleProp, ViewStyle} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Tooltip from '@/Atoms/Tooltip';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

interface Props {
  /** MaterialCommunityIcons glyph name. */
  icon: string;
  /** Hover tooltip text. */
  tooltip: string;
  onPress: () => void;
  disabled?: boolean;
  iconColor: string;
  iconSize?: number;
  style?: StyleProp<ViewStyle>;
}

/**
 * Icon-only classroom action button with a hover tooltip.
 *
 * Reuses the shared `Atoms/Tooltip` (react-tiny-popover). `Tooltip.toggle()` is
 * flip-only (no explicit open/close) and the atom also auto-closes after a
 * safety timeout — so wiring BOTH onHoverIn and onHoverOut straight to `toggle`
 * desyncs (the flips get out of phase → tooltip shows on hover-OUT, the
 * inverted behaviour we saw). We instead track desired hover state in a ref and
 * only call `toggle` when it would actually move in the intended direction
 * (open on enter if not already shown, close on leave if shown). A Pressable
 * (NOT Box) is used because RNW fires onHoverIn/onHoverOut reliably on it.
 * Hover is disabled on mobile/tablet (no hover surface).
 */
export default function IconTooltipButton({
  icon,
  tooltip,
  onPress,
  disabled,
  iconColor,
  iconSize = 18,
  style,
}: Props) {
  const {isMobileOrTab} = useMediaQuery();
  // Tracks whether WE last drove the tooltip open, so a stray/duplicate hover
  // event can't flip it the wrong way.
  const shownRef = React.useRef(false);
  return (
    <Tooltip
      text={tooltip}
      children={(toggle: () => void) => {
        const show = () => {
          if (isMobileOrTab || shownRef.current) {
            return;
          }
          shownRef.current = true;
          toggle();
        };
        const hide = () => {
          if (isMobileOrTab || !shownRef.current) {
            return;
          }
          shownRef.current = false;
          toggle();
        };
        return (
          <Pressable
            onPress={disabled ? undefined : onPress}
            onHoverIn={show}
            onHoverOut={hide}
            accessibilityLabel={tooltip}
            style={[
              {alignItems: 'center', justifyContent: 'center'},
              style as ViewStyle,
            ]}>
            <MaterialCommunityIcons
              name={icon}
              size={iconSize}
              color={iconColor}
            />
          </Pressable>
        );
      }}
    />
  );
}
